import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import BusinessIcon from '@mui/icons-material/Business';
import { css } from '@emotion/react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { PATH_ORGANIZATION_DEFAULT } from '../../app/constants/paths.constant';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import {
  formatOrganizationIdentifierValue,
  getOrganizationIdentifierLabel,
} from '../../utils/format-organization-identifier.util';

import { GenericStatusIndicator } from '../GenericStatusIndicator';
import { Avatar } from '../Avatar';
import { thirdPartyTooltipOf } from '../../utils/third-party-tooltip.util';

export const OrganizationCardCmp_Organization_Fragment = graphql(`
  fragment OrganizationCardCmp_Organization_Fragment on Organization {
    _id
    color
    logoImageUri
    name
    identifier {
      kind
      value
    }
    statusId
    tradingName
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationCardCmp_Organization_Fragment
  >;
}

export const OrganizationCard = ({
  organization: pOrganization,
}: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationCardCmp_Organization_Fragment,
    pOrganization,
  );

  const path = useBuildPath(PATH_ORGANIZATION_DEFAULT, {
    id: organization._id,
  });

  return (
    <Grid
      size={{
        lg: 6,
        xs: 12,
      }}
      sx={{ flexGrow: 1 }}
    >
      <Card>
        <CardHeader
          action={
            <GenericStatusIndicator
              statusId={organization.statusId as GenericStatusId}
              variant="icon"
            />
          }
          avatar={
            <Avatar
              bgColor={organization.color}
              icon={<BusinessIcon />}
              imageUri={organization.logoImageUri}
              label={thirdPartyTooltipOf(organization.name)}
            />
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
          subheader={getOrganizationIdentifierLabel(organization.identifier)}
          title={
            formatOrganizationIdentifierValue(organization.identifier) ??
            'Inconnu'
          }
        />
        <CardContent>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {organization.name}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Raison Sociale
          </Typography>
          {!!organization.tradingName && (
            <Typography variant="body2">
              Nom commercial :
              <br />
              {organization.tradingName}
            </Typography>
          )}
        </CardContent>
        <CardActions>
          <Button component={Link} size="small" to={path} variant="outlined">
            Consulter
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};
